import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { updateProfileApi } from 'features/users/users.api';

import ConfirmationPopup from 'components/popup/ConfirmationPopup';

const AccountSettings = ({ user, userId }) => {
  const dispatch = useDispatch();
  const bgCard = useColorModeValue('white', 'whiteAlpha.100');
  const textColor = useColorModeValue('gray.700', 'white');

  const { updateProfileStatus } = useSelector((state) => state.users);

  const [userData, setUserData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  const [errors, setErrors] = useState({});
  const [isPopupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setUserData({
        username: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!userData.username.trim()) newErrors.username = 'Username is required.';
    if (!userData.email.trim()) newErrors.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(userData.email))
      newErrors.email = 'Email is not valid.';
    if (!userData.firstName.trim()) newErrors.firstName = 'First Name is required.';
    if (!userData.lastName.trim()) newErrors.lastName = 'Last Name is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) setPopupOpen(true);
  };

  const handleConfirm = () => {
    dispatch(updateProfileApi({ userId, profileData: userData }));
    setPopupOpen(false);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  return (
    <Box
      bg={bgCard}
      p={6}
      borderRadius="lg"
      boxShadow="sm"
      display="flex"
      flexDirection="column"
    >
      <Heading as="h3" size="lg" mb={2} color={textColor}>
        Account Settings
      </Heading>
      <Text fontSize="sm" color="gray.500" mb={6}>
        Here you can change user account information
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <FormControl isInvalid={errors.username}>
          <FormLabel>Username</FormLabel>
          <Input
            name="username"
            value={userData.username}
            onChange={handleInputChange}
            placeholder="simmmple.web"
            color={textColor}
          />
          {errors.username && (
            <Text color="red.500" fontSize="sm">
              {errors.username}
            </Text>
          )}
        </FormControl>

        <FormControl isInvalid={errors.email}>
          <FormLabel>Email Address</FormLabel>
          <Input
            name="email"
            value={userData.email}
            onChange={handleInputChange}
            placeholder="mail@simmmple.com"
            color={textColor}
          />
          {errors.email && (
            <Text color="red.500" fontSize="sm">
              {errors.email}
            </Text>
          )}
        </FormControl>

        <FormControl isInvalid={errors.firstName}>
          <FormLabel>First Name</FormLabel>
          <Input
            name="firstName"
            value={userData.firstName}
            onChange={handleInputChange}
            placeholder="John"
            color={textColor}
          />
          {errors.firstName && (
            <Text color="red.500" fontSize="sm">
              {errors.firstName}
            </Text>
          )}
        </FormControl>

        <FormControl isInvalid={errors.lastName}>
          <FormLabel>Last Name</FormLabel>
          <Input
            name="lastName"
            value={userData.lastName}
            onChange={handleInputChange}
            placeholder="William"
            color={textColor}
          />
          {errors.lastName && (
            <Text color="red.500" fontSize="sm">
              {errors.lastName}
            </Text>
          )}
        </FormControl>
      </SimpleGrid>

      <Flex justify="flex-end" mt="auto">
        <Button
          colorScheme="blue"
          w="200px"
          onClick={handleSave}
          isLoading={updateProfileStatus === 'pending'}
        >
          Save
        </Button>
      </Flex>
      <ConfirmationPopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        onConfirm={handleConfirm}
        title="Confirm Changes"
        description="Are you sure you want to save these changes?"
        confirmButtonText="Yes, Save"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default AccountSettings;
